/** Small shared helpers. */

import clsx, { type ClassValue } from 'clsx'

import type { AtsType, CompanyCategory, CompanyStatus, Market } from '@/api/types'

export const cn = (...inputs: ClassValue[]): string => clsx(inputs)

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const seconds = Math.round((Date.now() - then) / 1000)
  const future = seconds < 0
  const abs = Math.abs(seconds)
  if (abs < 45) return future ? 'in a moment' : 'just now'
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ]
  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  for (const [unit, perUnit] of units) {
    if (abs >= perUnit) {
      const n = Math.floor(abs / perUnit)
      return formatter.format(future ? n : -n, unit)
    }
  }
  return 'just now'
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(iso),
  )
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

export const formatNumber = (n: number | null | undefined): string =>
  n == null ? '—' : new Intl.NumberFormat('en-IN').format(n)

export function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms} ms`
  const s = ms / 1000
  if (s < 60) return `${s.toFixed(1)} s`
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

export function formatSalary(
  min: string | null,
  max: string | null,
  currency: string | null,
  period: string | null,
): string | null {
  const lo = min == null ? null : Number(min)
  const hi = max == null ? null : Number(max)
  if (lo == null && hi == null) return null
  const fmt = (value: number) => {
    try {
      return new Intl.NumberFormat('en', {
        style: 'currency',
        currency: currency || 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(value)
    } catch {
      return `${value.toLocaleString()} ${currency ?? ''}`.trim()
    }
  }
  const range = lo != null && hi != null && lo !== hi ? `${fmt(lo)} – ${fmt(hi)}` : fmt((lo ?? hi) as number)
  return period ? `${range} / ${period.replace(/^per\s+/i, '')}` : range
}

export function hostOf(url: string | null | undefined): string {
  if (!url) return ''
  try {
    return new URL(url).host.replace(/^www\./, '')
  } catch {
    return url
  }
}

export const MARKET_LABEL: Record<Market, string> = { IN: 'India', US: 'United States' }
export const MARKET_FLAG: Record<Market, string> = { IN: '🇮🇳', US: '🇺🇸' }

export const ATS_LABEL: Record<AtsType, string> = {
  greenhouse: 'Greenhouse',
  lever: 'Lever',
  ashby: 'Ashby',
  workday: 'Workday',
  smartrecruiters: 'SmartRecruiters',
  workable: 'Workable',
  recruitee: 'Recruitee',
  bamboohr: 'BambooHR',
  oracle: 'Oracle Recruiting',
  generic: 'Custom site',
  unknown: 'Not detected yet',
}

export const ATS_TYPES = Object.keys(ATS_LABEL) as AtsType[]

export function humanise(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const CATEGORY_LABEL: Record<CompanyCategory, string> = {
  mnc: 'MNC',
  gcc: 'GCC',
  domestic: 'Domestic',
  startup: 'Startup',
}

export const CATEGORY_HELP: Record<CompanyCategory, string> = {
  mnc: 'Multinational company operating in the country',
  gcc: "Global Capability Centre — a foreign company's own tech / operations centre in India",
  domestic: 'Home-grown company of that country (Indian company in India, US company in the US)',
  startup: 'Startup or scale-up',
}

export const CATEGORIES = Object.keys(CATEGORY_LABEL) as CompanyCategory[]

export const TIER_HELP: Record<number, string> = {
  1: 'Level 1 — top brands / largest employers',
  2: 'Level 2 — established mid-to-large companies',
  3: 'Level 3 — smaller or emerging companies',
}

export const COMPANY_STATUS_LABEL: Record<CompanyStatus, string> = {
  no_career_page: 'No career page',
  pending: 'Waiting for first check',
  active: 'Checked OK',
  failing: 'Check failing',
  paused: 'Scheduler off',
}

export const INTERVALS: Array<[number, string]> = [
  [3, 'Every 3 hours'],
  [6, 'Every 6 hours'],
  [12, 'Every 12 hours'],
  [24, 'Every day'],
  [48, 'Every 2 days'],
  [168, 'Every week'],
]

export function experienceLabel(min: number | null, max: number | null): string | null {
  if (min == null) return null
  if (max != null && max !== min) return `${min}–${max} yrs`
  return min === 0 ? 'Fresher' : `${min}+ yrs`
}

/** [key, label, "from,to"] on the minimum years a posting asks for (same bands as the Overview). */
export const EXPERIENCE_BUCKETS: Array<[string, string, string]> = [
  ['0-5', '0 – 5 yrs', '0,4'],
  ['5-10', '5 – 10 yrs', '5,9'],
  ['10-15', '10 – 15 yrs', '10,14'],
  ['15-20', '15 – 20 yrs', '15,19'],
  ['20+', '20+ yrs', '20,40'],
]

export function intervalLabel(hours: number): string {
  return INTERVALS.find(([h]) => h === hours)?.[1] ?? `Every ${hours} h`
}
